import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import { StringParam, useQueryParam } from 'use-query-params';
import { Chart } from '@/features/details/charts/chart';
import { MetricKind } from '@/webapi/models';
import { Legend } from '@/features/details/charts/chart-legend';
import { VSpace } from '@/components/spacing';
import { Card } from '@/components/card';
import { breakpoints } from '@/components/responsive';
import {
  DailyPriceTestDay,
  usePriceTestReportApi,
} from '@/features/details/price-test-reports/api';
import {
  Experience,
  ExperienceVariant,
  useExperienceApi,
} from '@/webapi/use-experience-api';
import { DELETED } from '@/features/details/shared';
import {
  experiencePriceTestGoals,
  formatPriceTestGoal,
} from '@/features/details/price-test-reports/shared';
import { DatePickerContext } from '@/features/details/date-pick/context';
import { findActiveVariants } from '@/features/details/summary/context';
import { clone } from '@/features/editor/widgets/shared/modals/audience/facebook-audience/ad-search/shared';

interface DailyReportsProps {
  experience?: Experience;
  setExperience?: (e: Experience) => void;
}

interface DailyReportItem {
  date: string;
  [MetricKind.PRODUCT_SESSIONS]: number;
  [MetricKind.PRODUCT_ATC_RATE]: number;
  [MetricKind.PRODUCT_RETURNS]: number;
  [MetricKind.PRODUCT_RETURN_PERCENT]: number;
  [MetricKind.PRODUCT_PSV]: number;
  [MetricKind.PRODUCT_CR]: number;
  [MetricKind.PRODUCT_ITEMS_SOLD]: number;
  [MetricKind.PRODUCT_REVENUE]: number;
  [MetricKind.PRODUCT_PROFIT]: number;
  [MetricKind.PRODUCT_PROFIT_PERCENT]: number;
  [MetricKind.PRODUCT_PROFIT_PER_SESSION]: number;
  [MetricKind.PRODUCT_TRANSACTIONS]: number;
  [MetricKind.PRODUCT_IPP]: number;
  [MetricKind.PRODUCT_DETAILS_VIEWS_RATE]: number;
}

interface DailyChartData {
  name: string;
  data: DailyReportItem[];
}

export function DailyReports({ experience, setExperience }: DailyReportsProps) {
  const [dailyReport, setDailyReport] = useState<DailyPriceTestDay[]>([]);
  const { getDailyReportData, loading } = usePriceTestReportApi();
  const { loading: debounce, setExperienceGoals } = useExperienceApi(
    experience.lastDevicePreview,
    experience,
  );
  const [sessionType] = useQueryParam(`st`, StringParam);
  const [audience] = useQueryParam(`aud`, StringParam);
  const { version, startDate, endDate } = useContext(DatePickerContext);
  const activeVariants = findActiveVariants(experience, version);

  const [init, setInit] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setInit(true);
    }, 200);
  }, []);

  useAsyncEffect(
    async (isActive) => {
      const dailyReport = await getDailyReportData({
        startAt: startDate,
        endAt: endDate,
        expEndDate: experience?.endDate as unknown as Date,
        version: version?.version,
        experienceId: experience.id,
        activeVariants,
        sessionType,
        audience,
        init,
      });
      if (isActive() && dailyReport?.days?.length > 0) {
        setDailyReport(dailyReport.days);
      }
    },
    [
      JSON.stringify({
        version,
        id: experience.id,
        activeVariants,
        sessionType,
        audience,
        init,
        endDate,
        startDate,
      }),
    ],
  );

  const legend = getLegend(experience, activeVariants);
  const data = transformToChartData(experience.variants, dailyReport);
  const priceTestGoals = experiencePriceTestGoals(experience);

  return (
    <>
      {priceTestGoals.map((m) => (
        <>
          <DetailsCard data-title={m} key={`${m}chart`}>
            <Wrapper>
              <Title>
                <Row>{`${formatPriceTestGoal(m)}`}</Row>
                <Legend variations={legend} />
                <Hide
                  hidden={false}
                  onClick={async () => {
                    if (!debounce) {
                      const cloned = clone(experience) as Experience;
                      cloned.goals = (experience.goals || []).filter(
                        (metric) => metric !== m,
                      );
                      await setExperienceGoals(
                        experience.primaryGoal || MetricKind.PRODUCT_CR,
                        cloned.goals,
                      );
                      setExperience(cloned);
                    }
                  }}
                >
                  Hide
                </Hide>
              </Title>
              <Chart goal={m} data={data} loading={loading} />
            </Wrapper>
          </DetailsCard>
          <VSpace key={`${m}space`} value={2} />
        </>
      ))}
    </>
  );
}

const DetailsCard = styled(Card)`
  && {
    cursor: default;
    min-height: 300px;
    padding: 3.5rem;
    border-radius: 2rem;
    justify-content: center;
    align-items: center;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 5rem 1fr;
  grid-gap: 2rem;

  ${breakpoints.down(`md`)} {
    display: flex;
    flex-direction: column;
  }
`;

const Title = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0;
  font-family: Inter, serif;
  color: black;
  font-weight: 800;
  font-size: 1.8rem;

  ${breakpoints.down(`md`)} {
    font-size: 1.4rem;
    flex-direction: column;
    gap: 1.5rem;
    align-items: flex-start;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
`;

const Hide = styled.button`
  display: flex;
  z-index: 999;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: #f6f7f8;
  color: #656e72;
  border-radius: 25px;
  padding: 0.9rem 1rem;
  font-family: Inter, serif;
  font-size: 1.2rem;
  font-weight: 600;
  opacity: ${(p: { hidden: boolean }) => (p.hidden ? `0` : `1`)};

  :hover {
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.09), 0 1px 4px 0 rgba(7, 6, 6, 0.04);
    opacity: ${(p: { hidden: boolean }) => (p.hidden ? `0` : `0.5`)};
    cursor: ${(p: { hidden: boolean }) => (p.hidden ? `default` : `pointer`)};
  }

  :active {
    opacity: ${(p: { hidden: boolean }) => (p.hidden ? `0` : `1`)};
    cursor: default;
  }
`;

const transformToChartData = (
  variants: ExperienceVariant[],
  dailyReport: DailyPriceTestDay[],
): DailyChartData[] => {
  if (
    !variants ||
    !dailyReport ||
    variants.length === 0 ||
    dailyReport.length === 0
  ) {
    return [];
  }

  return variants.map((variant) => ({
    name: variant.name || variant.id,
    data: dailyReport.map((day) => {
      const vv = day.variants.find((v) => v.useCaseVariant === variant.id);
      if (!vv) {
        return {
          date: transformDate(day),
          [MetricKind.PRODUCT_SESSIONS]: 0,
          [MetricKind.PRODUCT_ATC_RATE]: 0,
          [MetricKind.PRODUCT_RETURNS]: 0,
          [MetricKind.PRODUCT_RETURN_PERCENT]: 0,
          [MetricKind.PRODUCT_PSV]: 0,
          [MetricKind.PRODUCT_CR]: 0,
          [MetricKind.PRODUCT_ITEMS_SOLD]: 0,
          [MetricKind.PRODUCT_REVENUE]: 0,
          [MetricKind.PRODUCT_PROFIT]: 0,
          [MetricKind.PRODUCT_PROFIT_PERCENT]: 0,
          [MetricKind.PRODUCT_PROFIT_PER_SESSION]: 0,
          [MetricKind.PRODUCT_TRANSACTIONS]: 0,
          [MetricKind.PRODUCT_IPP]: 0,
          [MetricKind.PRODUCT_DETAILS_VIEWS_RATE]: 0,
        };
      }

      const toNumber = (value: any) => (value || 0) * 1;
      const finite = (value: any) => (!Number.isFinite(value) ? 0 : value);

      return {
        date: transformDate(day),
        [MetricKind.PRODUCT_REVENUE]: toNumber(vv.revenue),
        [MetricKind.PRODUCT_TRANSACTIONS]: toNumber(vv.transactions),
        [MetricKind.PRODUCT_ITEMS_SOLD]: toNumber(vv.itemsSold),
        [MetricKind.PRODUCT_SESSIONS]: toNumber(vv.sessions),
        [MetricKind.PRODUCT_PSV]: finite(
          toNumber(vv.revenue) / toNumber(vv.sessions),
        ),
        [MetricKind.PRODUCT_CR]: finite(
          (toNumber(vv.transactions) / toNumber(vv.sessions)) * 100,
        ),
        [MetricKind.PRODUCT_IPP]: finite(
          toNumber(vv.itemsSold) / toNumber(vv.transactions),
        ),
        [MetricKind.PRODUCT_DETAILS_VIEWS_RATE]: toNumber(vv.pdv),
        [MetricKind.PRODUCT_ATC_RATE]: toNumber(vv.atcRate),
        [MetricKind.PRODUCT_PROFIT]: toNumber(vv.profit),
        [MetricKind.PRODUCT_PROFIT_PERCENT]: finite(
          toNumber(vv.profit) / toNumber(vv.revenue),
        ),
        [MetricKind.PRODUCT_PROFIT_PER_SESSION]: finite(
          toNumber(vv.profit) / toNumber(vv.sessions),
        ),
        [MetricKind.PRODUCT_RETURNS]: toNumber(vv.returns),
        [MetricKind.PRODUCT_RETURN_PERCENT]: finite(
          toNumber(vv.returns) / toNumber(vv.itemsSold),
        ),
      };
    }),
  }));
};

const transformDate = (day: DailyPriceTestDay) => {
  const date = new Date(day.date);

  return date.toLocaleDateString(`en-CA`);
};

const getLegend = (experience: Experience, activeVariants: string[]) =>
  experience.variants
    ?.filter((v) => activeVariants.includes(v.id))
    ?.map((v) => v.name || DELETED);
